import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import Button from '~/components/button';
import Container from '~/components/container';
import Loading from '~/components/loading';
import { useAuth } from '~/hooks/contexts/useAuth';
import useApi, { ApiMethod } from '~/hooks/useApi';
import Endpoints from '~/services/endpoints';
import { ActiveAgreement } from '..';
import { ClientDebtColumn, ClientHeader } from '../../shared/ClientPanels';
import { buildClientFields, summarizeInvoices } from '../../shared/clientSummary';
import { DebtItem, buildDebtGroups } from '../../shared/debt';
import NegotiationOffer from '../../shared/NegotiationOffer';

import * as St from '../../Client/styles';
import * as Tl from './styles';

interface AgreementClient {
  cartaoTeucard?: {
    limiteCreditoUtilizado?: number;
    limiteEmprestimoUtilizado?: number;
    limiteExtraUtilizado?: number;
    limiteTotalUtilizado?: number;
    numeroCartao?: string;
    status?: string;
  };
  compras?: DebtItem[];
  comprasLimite12x?: DebtItem[];
  cpf?: string;
  emprestimos?: DebtItem[];
  financiamentos?: DebtItem[];
  fotoCliente?: string;
  nome?: string;
  statusFatura?: string;
  valorTotalTransacoes?: number;
  valorUltimaFatura?: number;
  vencimentoFatura?: string;
}

interface PaymentOption {
  parcelas?: number;
  valorComDesconto?: number;
  valorDesconto?: number;
  valorOriginal?: number;
  valorParcela?: number;
  vencimento?: string;
}

interface NegotiationMarco {
  data?: string;
  nomeOperador?: string;
  tipo?: string;
}

interface Negotiation {
  marcos?: NegotiationMarco[];
  modalidade?: 'A_VISTA' | 'PARCELADO';
  nomeOperador?: string;
  opcaoPagamento?: PaymentOption;
  percentualDesconto?: number;
  vencimentoPrimeiroPagamento?: string;
}

type MarcoTone = 'gray' | 'red' | 'green';

const MARCO_LABELS: Record<
  string,
  { tone: MarcoTone; title: string; description: string }
> = {
  SIMULACAO_GERADA: {
    tone: 'gray',
    title: 'Simulação gerada',
    description: 'Simulação de acordo gerada com tabela padrão.',
  },
  NEGOCIACAO_CRIADA: {
    tone: 'red',
    title: 'Acordo confirmado',
    description: 'Acordo aceito e registrado pelo cliente',
  },
  NEGOCIACAO_CONFIRMADA: {
    tone: 'green',
    title: 'Entrada identificada e acordo ativo',
    description:
      'Pagamento da entrada compensado via Pix bancário e acordo efetivado.',
  },
  PARCELA_ATRASADA: {
    tone: 'red',
    title: 'Parcela atrasada',
    description: 'Parcela da negociação em atraso.',
  },
  PARCELA_REGULARIZADA: {
    tone: 'green',
    title: 'Parcela regularizada',
    description: 'Parcela da negociação regularizada.',
  },
  NEGOCIACAO_CONCLUIDA: {
    tone: 'green',
    title: 'Negociação concluída',
    description: 'Negociação concluída.',
  },
};

const toTimelineEvent = (marco: NegotiationMarco, operatorName?: string) => {
  const known = marco.tipo ? MARCO_LABELS[marco.tipo] : undefined;
  const operator = marco.nomeOperador?.trim() || operatorName?.trim();
  let description = known?.description || '';

  if (marco.tipo === 'SIMULACAO_GERADA' && operator) {
    description = `Operador ${operator} gerou simulação de acordo com tabela padrão.`;
  }

  return {
    tone: known?.tone || 'gray',
    date: marco.data ? moment(marco.data).format('DD/MM/YYYY [às] HH:mm') : '-',
    title: known?.title || marco.tipo || 'Marco',
    description,
  };
};

const readPayload = (payload: unknown) => {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const nested =
    record.data && typeof record.data === 'object'
      ? (record.data as Record<string, unknown>)
      : null;
  const source = record.consulta || record.negociacao ? record : nested;

  if (!source) return null;

  const consulta = source.consulta as { dadosConsulta?: AgreementClient } | undefined;
  const dadosConsulta =
    consulta?.dadosConsulta ||
    (source.cartaoTeucard || source.nome ? (source as AgreementClient) : undefined);

  return {
    client: dadosConsulta || null,
    negotiation: (source.negociacao as Negotiation) || null,
  };
};

const DebtNegotiationActiveAgreementDetail = (): JSX.Element => {
  const history = useHistory<ActiveAgreement>();
  const agreement = history.location.state;
  const { user } = useAuth();
  const [client, setClient] = useState<AgreementClient | null>(null);
  const [negotiation, setNegotiation] = useState<Negotiation | null>(null);
  const [feedback, setFeedback] = useState('');
  const getNegotiationDetail = useApi(
    Endpoints.debtNegotiation.detailNegotiation,
    ApiMethod.POST,
  );

  useEffect(() => {
    const loadDetail = async () => {
      const idNegociacao = agreement?.idNegociacao;
      const cpfOperador = String(user?.cpf || '').replace(/\D/g, '');

      if (!idNegociacao || !cpfOperador) {
        if (!idNegociacao) setFeedback('Negociação não informada.');
        return;
      }

      try {
        const response = await getNegotiationDetail.callApi({
          idNegociacao,
          cpfOperador,
        });

        if (!(response.status >= 200 && response.status < 300)) {
          setFeedback(
            response.mensagem || 'Não foi possível consultar o acordo.',
          );
          return;
        }

        const payload = readPayload(response.data);

        if (!payload?.client && !payload?.negotiation) {
          setFeedback('A consulta não retornou os dados do acordo.');
          return;
        }

        setFeedback('');
        setClient(payload?.client || null);
        setNegotiation(payload?.negotiation || null);
      } catch (error) {
        setFeedback(
          error instanceof Error
            ? error.message
            : 'Não foi possível consultar o acordo.',
        );
      }
    };

    loadDetail();
  }, [agreement?.idNegociacao, user?.cpf]);

  const option = negotiation?.opcaoPagamento;
  const negotiationType =
    negotiation?.modalidade === 'PARCELADO' ? 'parcelado' : 'avista';
  const discount = negotiation?.percentualDesconto;
  const dueDateValue = negotiation?.vencimentoPrimeiroPagamento || option?.vencimento;
  const entryValue = option?.valorParcela;
  const installmentValue = option?.valorParcela;
  const appliedDiscount = option?.valorDesconto;
  const debtTotal = option?.valorOriginal;
  const installments = option?.parcelas;
  const timeline = Array.isArray(negotiation?.marcos)
    ? negotiation.marcos.map((marco) =>
        toTimelineEvent(marco, negotiation?.nomeOperador),
      )
    : [];
  const card = client?.cartaoTeucard;
  const debtGroups = buildDebtGroups(client);
  const invoice = summarizeInvoices(
    client?.valorUltimaFatura,
    client?.valorTotalTransacoes,
  );
  const fields = buildClientFields(client?.nome || agreement?.nome, card);

  return (
    <Container>
      <Loading status={getNegotiationDetail.loading} />
      {feedback && <St.Feedback>{feedback}</St.Feedback>}
      <ClientHeader
        photo={client?.fotoCliente}
        name={client?.nome || agreement?.nome}
        fields={fields}
      />

      <St.Boxes>
        <ClientDebtColumn
          dueDate={client?.vencimentoFatura}
          invoiceStatus={client?.statusFatura}
          currentInvoice={invoice.currentInvoice}
          futureInvoices={invoice.futureInvoices}
          debtTotal={invoice.debtTotal}
          groups={debtGroups}
        />
        <St.SideColumn>
          <St.Panel>
            <St.ConfigStack>
              <div>
                <St.PanelTitle>Configurar negociação</St.PanelTitle>
                <St.ConfigBlock>
                  <NegotiationOffer
                    type={negotiationType}
                    entryValue={entryValue}
                    discountValue={discount === undefined ? '' : String(discount)}
                    installments={installments}
                    installmentValue={installmentValue}
                    invoiceValue={entryValue}
                    dueDate={dueDateValue || ''}
                    appliedDiscount={appliedDiscount}
                    debtTotal={debtTotal}
                  />
                </St.ConfigBlock>
              </div>
            </St.ConfigStack>
          </St.Panel>
          <St.Panel>
            <St.PanelTitle>Histórico negociação</St.PanelTitle>
            <Tl.Timeline>
              {timeline.length === 0 && (
                <Tl.EventDescription>Nenhum marco registrado.</Tl.EventDescription>
              )}
              {timeline.map((event, index) => (
                <Tl.Event key={`${event.title}-${event.date}`}>
                  <Tl.Rail>
                    <Tl.Dot $tone={event.tone} />
                    {index < timeline.length - 1 && <Tl.Line />}
                  </Tl.Rail>
                  <Tl.EventBody>
                    <Tl.EventDate>{event.date}</Tl.EventDate>
                    <Tl.EventTitle $tone={event.tone}>{event.title}</Tl.EventTitle>
                    {event.description && (
                      <Tl.EventDescription>{event.description}</Tl.EventDescription>
                    )}
                  </Tl.EventBody>
                </Tl.Event>
              ))}
            </Tl.Timeline>
            <Tl.Actions>
              <Button
                fullWidth
                type="button"
                onClick={() => history.push('/negociacao-dividas/buscar-cliente')}
              >
                <span>Simular nova negociação</span>
              </Button>
              <Button
                fullWidth
                outlined
                type="button"
                onClick={() =>
                  history.push(
                    history.location.pathname.includes('historico')
                      ? '/negociacao-dividas/historico'
                      : '/negociacao-dividas/acordos-ativos',
                  )
                }
              >
                <span>Voltar</span>
              </Button>
            </Tl.Actions>
          </St.Panel>
        </St.SideColumn>
      </St.Boxes>
    </Container>
  );
};

export default DebtNegotiationActiveAgreementDetail;
